/* eslint-disable */
import Vue from 'vue';
import axios from 'axios';
import _ from 'lodash';
import { serverTimestamp, arrayUnion, arrayRemove, doc, updateDoc } from 'firebase/firestore';
import { detectInboundSMSNotification } from '@/utils/smsNotificationDetection';
import { shouldHideRoomFromChatList, previewForDisplay, computeRoomUnreadCount } from './conversationsV2-filters';
import { classifyNonDelivery } from '@/utils/messaging';
import { optOutResultFromSendFailure } from '@/utils/textConsent';
import { expandMediaMessage } from '@/utils/legacyMedia';
import { tagSimulatedSend } from '@/utils/demo/demoShell';
import { coerceToMs } from '@/utils/firestoreTime';
import { buildDeepLinkedRoom } from '@/utils/conversationDeepLink';
import { safeSnapshot } from '@/store/firestoreListen';

export default {
  namespaced: true,
  state: {
    rooms: [],
    roomsLoaded: false,
    loadingRooms: true,
    messages: [],
    messagesLoaded: false,
    listeners: [],
    roomsListener: null,
    messageListener: null,
    currentUserId: null,
    dateFormatStr: 'MM/DD/YY',
    localRooms: {},
    selectedRoomId: null,
    unreadBaselineMs: null,
    lastReadMsByRoom: {},
    newConversationMsgTimestamp: 0,
    latestInboundMessage: null,
    _isFirstRoomsSnapshot: true,
    _prevRoomLastMsgTs: {},
    // REMOVED: Preview system state - No longer needed after backend fix
    // Patient SMS Communication Widget State
    patientSmsMode: false,
    patientSmsWidgetOpen: false,
    patientSmsWidgetMinimized: false,
    currentPatientForSms: null,
    patientSmsConversation: null,
    patientSmsMessages: [],
    patientSmsMessagesLoaded: false,
    patientSmsListener: null,
  },
  mutations: {
    setRooms(state, payload) {
      // FIX (Jan 23, 2026): Debounce rapid setRooms calls to prevent flickering
      // FIX (Mar 13, 2026): Check content changes (lastMessage, unreadCount) not just room order
      // Previous version only compared top 3 room IDs, silently dropping updates where
      // lastMessage.content or unreadCount changed but room order stayed the same.
      const now = Date.now();
      const lastCall = state._lastSetRoomsTime || 0;
      const timeSinceLastCall = now - lastCall;

      // Helper: build a fingerprint of room content that matters for display
      const roomFingerprint = (rooms) => {
        if (!rooms || !rooms.length) return '';
        return rooms.slice(0, 10).map(r =>
          `${r.roomId}:${r.unreadCount || 0}:${(r.lastMessage?.content || r.lastMessagePreview || '').substring(0, 30)}:${r.lastMessage?.timestampNum || 0}`
        ).join('|');
      };

      const shouldDebounce = (state._roomSelectionInProgress || timeSinceLastCall < 250)
        && state.rooms && state.rooms.length > 0
        && payload && payload.length > 0;

      if (shouldDebounce) {
        const oldFp = roomFingerprint(state.rooms);
        const newFp = roomFingerprint(payload);
        if (oldFp === newFp) {
          return; // Truly identical — skip to prevent flickering
        }
      }

      state._lastSetRoomsTime = now;
      state.rooms = payload;
    },
    setRoomsLoaded(state, payload) { state.roomsLoaded = payload; },
    setLoadingRooms(state, payload) { state.loadingRooms = payload; },
    setMessages(state, payload) { state.messages = payload; },
    setMessagesLoaded(state, payload) { state.messagesLoaded = payload; },
    addMessage(state, message) {
      if (!message) return;
      state.messages = [...state.messages, message];
    },
    updateMessage(state, { messageId, message }) {
      const index = state.messages.findIndex(m => m._id === messageId || m.id === messageId);
      if (index !== -1) {
        const updatedMessages = [...state.messages];
        updatedMessages[index] = { ...updatedMessages[index], ...message };
        state.messages = updatedMessages;
      }
    },
    removeMessage(state, messageId) {
      state.messages = state.messages.filter(m => m._id !== messageId && m.id !== messageId);
    },
    addListener(state, payload) { state.listeners.push(payload); },
    setListeners(state, payload) { state.listeners = payload; },
    setRoomsListener(state, payload) { state.roomsListener = payload; },
    setMessageListener(state, payload) { state.messageListener = payload; },
    // REMOVED: Preview listeners - No longer needed after backend fix
    setCurrentUserId(state, payload) { state.currentUserId = payload; },
    setSelectedRoomId(state, payload) {
      // FIX (Jan 23, 2026): Prevent redundant selection of the same room
      if (state.selectedRoomId === payload) {
        return;
      }
      state.selectedRoomId = payload;

      // FIX (Jan 23, 2026): Set flag to suppress redundant setRooms during selection
      // This prevents flickering when selecting a room triggers multiple Firestore updates
      state._roomSelectionInProgress = true;
      setTimeout(() => {
        state._roomSelectionInProgress = false;
      }, 500);
    },
    setUnreadBaselineMs(state, payload) { state.unreadBaselineMs = payload; },
    setNewConversationMsgTimestamp(state, payload) { state.newConversationMsgTimestamp = payload; },
    setLatestInboundMessage(state, payload) { state.latestInboundMessage = payload; },
    setIsFirstRoomsSnapshot(state, payload) { state._isFirstRoomsSnapshot = payload; },
    setPrevRoomLastMsgTs(state, { roomId, ts }) {
      if (!roomId) return;
      state._prevRoomLastMsgTs = { ...state._prevRoomLastMsgTs, [roomId]: ts };
    },
    setLastReadMs(state, { roomId, ms }) {
      if (!roomId) return;
      const next = { ...state.lastReadMsByRoom, [roomId]: ms };
      state.lastReadMsByRoom = next;
      try {
        const siteID = (this.state && this.state.customer && this.state.customer.siteID) || 'site';
        const userID = (this.state && this.state.customer && this.state.customer.userID) || 'user';
        const key = `convV2LastRead:${siteID}:${userID}`;
        localStorage.setItem(key, JSON.stringify(next));
      } catch(_) {}
    },
    clearLastReadMs(state, { roomId }) {
      if (!roomId) return;
      const next = { ...state.lastReadMsByRoom };
      delete next[roomId];
      state.lastReadMsByRoom = next;
      try {
        const siteID = (this.state && this.state.customer && this.state.customer.siteID) || 'site';
        const userID = (this.state && this.state.customer && this.state.customer.userID) || 'user';
        const key = `convV2LastRead:${siteID}:${userID}`;
        localStorage.setItem(key, JSON.stringify(next));
      } catch(_) {}
    },
    setRoomUnreadCount(state, { roomId, count }) {
      const idx = state.rooms.findIndex(r => r.roomId === roomId);
      if (idx !== -1) {
        const newCount = Number.isFinite(count) ? count : 0;
        const base = state.rooms[idx] || {};
        // Skip update if count hasn't changed - prevents unnecessary re-renders/flickering
        if (base.unreadCount === newCount) return;
        const updated = { ...base, unreadCount: newCount, attributes: { ...(base.attributes || {}), unreadCount: newCount } };
        const newRooms = state.rooms.slice();
        newRooms[idx] = updated;
        state.rooms = newRooms;
      }
    },
    setLocalRoom(state, room) {
      if (!room || !room.roomId) return;
      state.localRooms[room.roomId] = room;
      // Also insert at top of visible rooms list immediately
      const idx = state.rooms.findIndex(r => r.roomId === room.roomId);
      if (idx === -1) {
        state.rooms = [room, ...state.rooms];
      }
    },
    removeLocalRoom(state, roomId) {
      if (!roomId) return;
      delete state.localRooms[roomId];
    },
    upsertRoomToVisibleList(state, room) {
      if (!room || !room.roomId) return;
      const idx = state.rooms.findIndex(r => r.roomId === room.roomId);
      if (idx === -1) {
        state.rooms = [room, ...state.rooms];
      } else {
        const newRooms = state.rooms.slice();
        newRooms[idx] = { ...newRooms[idx], ...room };
        state.rooms = newRooms;
      }
    },
    // REMOVED: sortRoomsByActivity mutation - vue-advanced-chat sorts by 'index' property automatically
    // REMOVED: Preview request tracking - No longer needed after backend fix
    setMessageFile(state, { messageId, file }) {
      if (!messageId || !file) return;
      // Update currently loaded messages list
      try {
        const idx = (state.messages || []).findIndex(m => m && (m._id === messageId || m.id === messageId));
        if (idx !== -1) {
          const normalize = (f) => {
            try {
              const fromMime = (t) => {
                if (!t || typeof t !== 'string') return null;
                const parts = t.split('/');
                return parts.length > 1 ? parts[1] : parts[0];
              };
              const mimeExt = fromMime(f.type);
              const nameExt = (typeof f.name === 'string' && f.name.includes('.')) ? f.name.split('.').pop() : null;
              const ext = (mimeExt && mimeExt.length < 10 ? mimeExt : null) || nameExt || f.type || '';
              return { name: f.name, size: f.size, url: f.url, type: ext };
            } catch (_) { return f; }
          };
          const copy = state.messages.slice();
          copy[idx] = { ...copy[idx], file: normalize(file) };
          state.messages = copy;
        }
      } catch (_) {}
    },
    // Patient SMS Communication Widget Mutations
    setPatientSmsMode(state, payload) { state.patientSmsMode = payload; },
    setPatientSmsWidgetOpen(state, payload) { state.patientSmsWidgetOpen = payload; },
    setPatientSmsWidgetMinimized(state, payload) { state.patientSmsWidgetMinimized = payload; },
    setCurrentPatientForSms(state, payload) { state.currentPatientForSms = payload; },
    setPatientSmsConversation(state, payload) { state.patientSmsConversation = payload; },
    setPatientSmsMessages(state, payload) { state.patientSmsMessages = payload; },
    setPatientSmsMessagesLoaded(state, payload) { state.patientSmsMessagesLoaded = payload; },
    setPatientSmsListener(state, payload) { state.patientSmsListener = payload; },
    addPatientSmsMessage(state, message) {
      if (!message) return;
      state.patientSmsMessages = [...state.patientSmsMessages, message];
    },
    updatePatientSmsConversationUnread(state, count) {
      if (state.patientSmsConversation) {
        state.patientSmsConversation = {
          ...state.patientSmsConversation,
          unreadCount: count || 0
        };
      }
    },
  },
  actions: {
    initUnreadBaseline({ commit, rootState }) {
      try {
        const siteID = rootState.customer?.siteID || 'site';
        const userID = rootState.customer?.userID || 'user';
        const key = `convV2UnreadBaseline:${siteID}:${userID}`;
        let ms = localStorage.getItem(key);
        if (!ms) {
          ms = Date.now();
          localStorage.setItem(key, String(ms));
        } else {
          ms = parseInt(ms, 10);
          if (!Number.isFinite(ms)) ms = Date.now();
        }
        commit('setUnreadBaselineMs', ms);
      } catch(_) {}
    },
    // skipIndexUpdate: when true, don't change the room's index (for viewing existing messages)
    // When false/undefined, update index for re-sorting (for new incoming messages)
    updateRoomLastMessageLocally({ commit, rootState, state }, { roomId, content, ts, skipIndexUpdate }) {
      try {
        if (!roomId || !content) return;
        const when = Number.isFinite(ts) ? ts : Date.now();
        // Settings live at customer.settings (array); there is no customer.allSettings.
        const tz = (rootState.customer?.settings || []).find(s => s.Property === 'GENERAL_TIMEZONE')?.PropertyValue || 'America/New_York';
        const formatted = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(when)).replace(',', '');
        const partial = {
          roomId,
          dateUpdated: new Date(when),
          lastMessagePreview: content,
          lastMessage: {
            _id: `local-${when}`,
            content,
            text: content,
            message: content,
            timestampNum: when,
            date: when,
            timestamp: formatted
          }
        };
        // Only update index when new messages arrive (skipIndexUpdate=false/undefined)
        // Don't update when user is just viewing existing messages (skipIndexUpdate=true)
        // This prevents scroll jumping when clicking on a conversation
        if (!skipIndexUpdate) {
          // CRITICAL: Update index for vue-advanced-chat sorting (higher = first)
          partial.index = when;
        }
        commit('upsertRoomToVisibleList', partial);
        // Note: vue-advanced-chat sorts rooms by 'index' property automatically
      } catch(_) {}
    },
    // Fetch actual latest message timestamps from messages subcollection to fix stale lastMessage/dateUpdated
    // FIXED: Batch updates to prevent flickering (was updating rooms one-by-one causing multiple re-renders)
    // REMOVED (Apr 2026): refreshRoomTimestampsFromMessages was fetching the latest message
    // from each room's messages subcollection to "fix" stale timestamps. However, old messages
    // had dateCreated as String while new ones use Firestore Timestamp. Firestore orderBy with
    // mixed types sorts Strings before Timestamps, so the query returned old October messages
    // instead of new April ones. The parent conversation doc (updated by saveConvoMsgToFirebase)
    // already has correct lastMessage data — the subcollection fetch was redundant and harmful.
    loadLastReadMap({ commit, rootState }) {
      try {
        const siteID = rootState.customer?.siteID || 'site';
        const userID = rootState.customer?.userID || 'user';
        const key = `convV2LastRead:${siteID}:${userID}`;
        const raw = localStorage.getItem(key);
        if (raw) {
          const obj = JSON.parse(raw);
          if (obj && typeof obj === 'object') {
            Object.keys(obj).forEach(roomId => {
              const ms = parseInt(obj[roomId], 10);
              if (Number.isFinite(ms)) commit('setLastReadMs', { roomId, ms });
            });
          }
        }
      } catch(_) {}
    },
    recordRoomRead({ commit }, { roomId, ms }) {
      const when = Number.isFinite(ms) ? ms : Date.now();
      commit('setLastReadMs', { roomId, ms: when });
    },
    initRoomsListener({ commit, dispatch, rootState }, { siteID, cutoffDays = 21, limitCount = 100 }) {
      // Prevent duplicate listeners
      if (this.state.conversationsV2?.roomsListener) {
        try {
          this.state.conversationsV2.roomsListener();
          commit('setRoomsListener', null);
          setTimeout(() => {
            this.dispatch('conversationsV2/initRoomsListener', { siteID, cutoffDays, limitCount });
          }, 100);
          return;
        } catch (_) {}
      }

      // REMOVED (Apr 2026): isFirstSnapshot was used to trigger refreshRoomTimestampsFromMessages
      // on initial load. That function has been removed — parent doc is the source of truth.

      const { collection, query, where, orderBy, limit, db } = rootState;
      if (!db) {
        try { console.error('[ConversationsV2] Firestore DB missing from rootState'); } catch (_) {}
        commit('setLoadingRooms', false);
        commit('setRoomsLoaded', true);
        return;
      }
      const cutoff = new Date(Date.now() - cutoffDays * 24 * 60 * 60 * 1000);
      let q;
      try {
        q = query(
          collection(db, 'conversations'),
          where('siteID', '==', siteID),
          where('dateUpdated', '>=', cutoff),
          orderBy('dateUpdated', 'desc'),
          limit(limitCount)
        );
      } catch (e) {
        try { console.error('[ConversationsV2] Query build failed, falling back without cutoff/order', e); } catch (_) {}
        q = query(
          collection(db, 'conversations'),
          where('siteID', '==', siteID)
        );
      }
      const unsubscribe = safeSnapshot(q, (snap) => {
        const rooms = [];
        snap.forEach(docSnap => {
          const data = docSnap.data() || {};
          // Adapter for vue-advanced-chat v0.6.3
          data.roomId = docSnap.id;
          data.sid = docSnap.id;
          // RESTORED: Comprehensive patient name fallback - Firestore data still has gaps
          const parseAttrs = (raw) => { try { if (!raw && raw !== 0) return {}; if (typeof raw === 'string') return JSON.parse(raw); if (typeof raw === 'object') return raw; return {}; } catch(_) { return {}; } };
          const attrs = parseAttrs(data.attributes);
          const isMeaningful = (v) => typeof v === 'string' && v.trim() && v.trim().toLowerCase() !== 'unknown patient';
          const looksLikePhone = (v) => typeof v === 'string' && /^(\+?\d[\d\s().-]{5,})$/.test(v.trim());
          
          // Priority-based name resolution
          const nameCandidate = isMeaningful(data.patientName) ? data.patientName
            : (isMeaningful(attrs.patientName) ? attrs.patientName
              : (isMeaningful(attrs.patient) ? attrs.patient
                : (isMeaningful(attrs.name) ? attrs.name
                  : (isMeaningful(data.friendlyName) && !looksLikePhone(data.friendlyName) ? data.friendlyName : null))));
          
          const phoneCandidate = [data.incomingPhoneNum, data.patientPhone, attrs.phone, data.lastMessage?.author]
            .find(v => looksLikePhone(v));
          
          // Use Vue.set to ensure reactivity for room name updates
          const finalRoomName = nameCandidate || phoneCandidate || data.incomingPhoneNum || 'Unknown Patient';
          Vue.set(data, 'roomName', finalRoomName);
          // Channel ('sms' | 'whatsapp', default 'sms' for legacy docs) drives the reply-channel
          // selector + the WhatsApp icon the chat page injects on WhatsApp room rows (by room id).
          Vue.set(data, 'channel', data.channel || 'sms');
          // Minimal users array for vue-advanced-chat compatibility
          if (!Array.isArray(data.users)) {
            const patientId = (data.incomingPhoneNum || data.patientPhone || 'patient');
            // RESTORED: Use same comprehensive name resolution as roomName
            const patientName = nameCandidate || phoneCandidate || patientId;
            Vue.set(data, 'users', [
              { _id: 'site', username: 'Site' },
              { _id: patientId, username: patientName }
            ]);
          }
          // Ensure users array fits vue-advanced-chat requirements to avoid watcher errors
          try {
            if (!Array.isArray(data.users) || data.users.length < 1) {
              const patientId = (data.incomingPhoneNum || data.patientPhone || 'patient');
              // Align with roomName logic for consistency
              const parseAttrs = (raw) => { try { if (!raw && raw !== 0) return {}; if (typeof raw === 'string') return JSON.parse(raw); if (typeof raw === 'object') return raw; return {}; } catch(_) { return {}; } };
              const attrs = parseAttrs(data.attributes);
              const isMeaningful = (v) => typeof v === 'string' && v.trim() && v.trim().toLowerCase() !== 'unknown patient';
              const looksLikePhone = (v) => typeof v === 'string' && /^(\+?\d[\d\s().-]{5,})$/.test(v.trim());
              const attrName = attrs.patientName || attrs.patient || attrs.name;
              const nameCandidate = isMeaningful(data.patientName)
                ? data.patientName
                : (isMeaningful(attrName) && !looksLikePhone(attrName) ? attrName
                  : (isMeaningful(data.friendlyName) && !looksLikePhone(data.friendlyName) ? data.friendlyName : null));
              const phoneCandidate = [data.incomingPhoneNum, data.patientPhone, attrs.phone, data.lastMessage?.author]
                .find(v => looksLikePhone(v));
              const patientName = nameCandidate || phoneCandidate || patientId;
              Vue.set(data, 'users', [
                { _id: 'site', username: 'Site' },
                { _id: patientId, username: patientName }
              ]);
            } else {
              Vue.set(data, 'users', data.users.map((u, idx) => {
                const id = (typeof u?._id === 'string' || typeof u?._id === 'number') ? u._id : (u?.id || (idx === 0 ? 'site' : (data.incomingPhoneNum || 'patient')));
                let name;
                if (typeof u?.username === 'string' && u.username) {
                  name = u.username;
                } else if (u?.name) {
                  name = u.name;
                } else if (idx === 0) {
                  name = 'Site';
                } else {
                  // RESTORED: Use comprehensive name resolution
                  name = nameCandidate || phoneCandidate || 'Patient';
                }
                return { _id: id, username: name };
              }));
            }
          } catch(_) {}
          // Compute activity time; PREFER true last message time over dateUpdated
          // coerceToMs now comes from @/utils/firestoreTime (imported above). It is the same logic
          // this file has always used, with one deliberate hardening: the `.toDate()` branch is
          // isNaN-guarded. That matters HERE because the result feeds `Math.max` into `index` —
          // a NaN would poison the room-list sort key. Real Timestamps are unaffected.
          // CRITICAL: Use Math.max to get the MOST RECENT timestamp
          // This ensures we always show the latest activity, whether from lastMessage or dateUpdated
          // Previous "fix" using priority order caused stale lastMessage timestamps to show old dates
          const lmList = [
            coerceToMs(data.lastMessage?.timestampNum),
            coerceToMs(data.lastMessage?.timestamp),
            coerceToMs(data.lastMessage?.date),
            coerceToMs(data.lastMessage?.dateCreated)
          ];
          const firstLm = lmList.find(v => typeof v === 'number' && !isNaN(v));
          const d1 = coerceToMs(data.dateUpdated);
          // `dateCreated` FIRST: it is the field every writer actually produces
          // (smsEngine.writeMessageToThread `if (isNew) convoDoc.dateCreated`, and the legacy
          // conversations-service). Nothing has ever written `createdAt`/`createdOn` on a
          // conversation doc — verified 2026-07-31 — so this term used to be permanently 0. That is
          // invisible on the happy path (the room query orders by `dateUpdated`, and Firestore drops
          // docs missing the ordered field) but real in the FALLBACK query below, which has neither
          // the cutoff nor the order: those docs arrived with ts 0, sorting to the bottom and
          // tripping the `ts && ts <= baseline` unread guards into a blank, badge-bearing row.
          // The legacy names are kept as a tail fallback — free, and harmless if a doc ever has one.
          const d2 = coerceToMs(data.dateCreated || data.createdAt || data.createdOn);
          // Use Math.max to always show the most recent timestamp
          // This is important because lastMessage can be stale if webhooks failed or data wasn't synced
          const ts = Math.max(
            typeof firstLm === 'number' && !isNaN(firstLm) ? firstLm : 0,
            typeof d1 === 'number' && !isNaN(d1) ? d1 : 0,
            typeof d2 === 'number' && !isNaN(d2) ? d2 : 0
          );
          const dateObj = ts ? new Date(ts) : null;
          data.lastMessage = data.lastMessage || {};
          // Ensure content exists to prevent RoomsList.getLastMessage from reading undefined.length
          if (typeof data.lastMessage.content !== 'string') {
            data.lastMessage.content = '';
          }
          if (typeof data.lastMessage.message !== 'string') {
            data.lastMessage.message = '';
          }
          if (typeof data.lastMessage.text !== 'string') {
            data.lastMessage.text = '';
          }
          if (typeof data.lastMessage._id !== 'string') {
            data.lastMessage._id = 'last-'+data.sid;
          }
          // Provide an explicit preview string used by templates
          data.lastMessagePreview = data.lastMessage.content || data.lastMessage.text || data.lastMessage.message || '';
          data.lastMessage.timestampNum = ts;
          data.lastMessage.date = ts;
          // CRITICAL FIX: vue-advanced-chat sorts rooms by 'index' property (higher = first)
          // Set index to timestamp so rooms are sorted by most recent activity
          data.index = ts;
          try {
            // Settings live at customer.settings (array); there is no customer.allSettings.
            const tz = (rootState.customer?.settings || []).find(s => s.Property === 'GENERAL_TIMEZONE')?.PropertyValue || 'America/New_York';
            data.lastMessage.timestamp = dateObj
              ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(dateObj).replace(',', '')
              : '';
          } catch (_) { data.lastMessage.timestamp = dateObj ? dateObj.toLocaleString() : ''; }
          // Compute unread using site-level lastRead from conversation doc (fallback to local map) and baseline.
          // Predicate is extracted to conversationsV2-filters.computeRoomUnreadCount (pure, unit-tested).
          // Unread = unviewed inbound exists (lastInboundAtMs vs siteLastReadAt), NOT "last message is inbound".
          // Manual outbound (profile / conversations / workflow / wiki) replaces lastMessage with
          // outbound:true but must not clear inbound unread — only opening the thread writes siteLastReadAt.
          const baseline = this.state.conversationsV2.unreadBaselineMs;
          // Shared coercer (see import). The isNaN guard on the `.toDate()` branch matters
          // especially here: the result is consumed below via `typeof === 'number'`, and NaN IS
          // typeof number — so before the guard a malformed value would have been accepted as a
          // valid "last read" and corrupted the unread count. null is correctly rejected.
          const siteLastReadMsFromDoc = coerceToMs(data.siteLastReadAt || data.lastViewedAt);
          const lastInboundAtMs = coerceToMs(data.lastInboundAtMs);
          const localMap = this.state.conversationsV2.lastReadMsByRoom || {};
          const lastReadFromMap = localMap[data.roomId];
          const lastRead = (typeof siteLastReadMsFromDoc === 'number') ? siteLastReadMsFromDoc : lastReadFromMap;
          // New message detection: last message authored by non-site user
          const isReminderMessage = data.lastMessage?.attributes && (
            (typeof data.lastMessage.attributes === 'string' ? JSON.parse(data.lastMessage.attributes) : data.lastMessage.attributes).reminderType ||
            (typeof data.lastMessage.attributes === 'string' ? JSON.parse(data.lastMessage.attributes) : data.lastMessage.attributes).autoReply
          );
          const lastBySite = (data.lastMessage && (
            data.lastMessage.author === this.state.customer?.userID || 
            data.lastMessage.author === this.state.customer?.user?.Name || 
            data.lastMessage.attributes?.outbound === true ||
            isReminderMessage  // Treat reminder messages as site messages
          ));
          // Check if conversation was manually marked as unread by this site
          // Shared coercer (see import). Consumed only as a truthiness check below, so the isNaN
          // guard is a no-op here (NaN and null are both falsy) — converged for consistency.
          const markedUnreadAt = coerceToMs(data.markedUnreadBySite);
          const isSelected = this.state.conversationsV2.selectedRoomId === data.roomId || this.state.conversationsV2.selectedRoomId === data.sid;
          let serverUnread = 0;
          try {
            serverUnread = (typeof data.unreadCount === 'number' ? data.unreadCount : (typeof data.attributes?.unreadCount === 'number' ? data.attributes.unreadCount : 0));
          } catch(_) {}
          const unread = computeRoomUnreadCount({
            lastInboundAtMs,
            lastMessageTs: ts,
            lastBySite,
            lastRead,
            baseline,
            isSelected,
            markedUnreadAt,
            serverUnread,
          });
          // CRITICAL: Use Vue.set for Vue 2 reactivity - direct assignment won't trigger updates
          Vue.set(data, 'unreadCount', unread);
          
          // --- CHAT LIST FILTERING (extracted to ./conversationsV2-filters for testability) ---
          //
          // Hides rooms that should not appear in the SMS Conversations chat list:
          //   1. No real preview content + no patient response  (Tanya #2 fix, 2026-05-21).
          //      Covers reminder-only conversations whose lastMessage is never set
          //      because the backend gate skips writes for automated messages.
          //   2. Stale reminderType in lastMessage from before the backend gate
          //      deployed (legacy defense-in-depth).
          //
          // Logic is pure-function in conversationsV2-filters.js — see node:test specs
          // in __tests__/conversationsV2-filters.test.js for exact behavior and the
          // bug history each clause defends against.
          if (shouldHideRoomFromChatList(data)) {
              return;
          }
          // --- END OF FILTERING LOGIC ---

          // --- BLANK-PREVIEW BACKSTOP (must stay BELOW the filter) ---
          // This room is already going to be shown. If its lastMessage.content was persisted empty
          // it would render as a nameless blank row — the Smile Bar bug (2026-07-31): a photo-only
          // inbound MMS whose S3 re-host failed wrote content:'', and unreadCount=1 defeated the
          // "no content AND no unread" hide rule above, so staff saw a row with a badge and no text.
          // Fixed at the source in smsEngine.previewContent; this covers docs written before that
          // shipped (21-day retention). vue-advanced-chat reads lastMessage.content directly (no
          // per-row slot), so the placeholder has to land there to actually render.
          // Order matters: applying this ABOVE the filter would make hasRealContent true and un-hide
          // every empty placeholder room the filter deliberately suppresses.
          // Unconditional: previewForDisplay returns real text unchanged and only substitutes when
          // the preview is empty OR whitespace-only (which renders just as blank).
          data.lastMessagePreview = previewForDisplay(data.lastMessagePreview);
          data.lastMessage.content = data.lastMessagePreview;
          // --- END BLANK-PREVIEW BACKSTOP ---
          
          // REMOVED: Frontend fallback logic - Firestore is the source of truth
          // All content issues should be fixed at the backend/Firestore level
          
          rooms.push(data);
        });
        rooms.sort((a, b) => {
          // NOT converged onto @/utils/firestoreTime — deliberately. This variant returns 0 (not
          // null) on failure and short-circuits on numbers, and it feeds a subtraction comparator
          // where 0 is the meaningful "sorts last" value. Swapping in the shared coercer would
          // change sort behaviour for unparseable values, which is a behaviour change, not a
          // cleanup. Left as-is on purpose. (2026-07-21 convergence pass.)
          const toMs = (v) => {
            if (typeof v === 'number' && !isNaN(v)) return v;
            if (v && typeof v.toDate === 'function') return v.toDate().getTime();
            if (v && typeof v._seconds === 'number') return v._seconds * 1000;
            const d = new Date(v); const ms = d.getTime();
            return isNaN(ms) ? 0 : ms;
          };
          // Use Math.max to get most recent activity for sorting (consistent with index calculation)
          const aLm = (a.lastMessage && typeof a.lastMessage.timestampNum === 'number') ? a.lastMessage.timestampNum : toMs(a.lastMessage?.timestamp || a.lastMessage?.date);
          const bLm = (b.lastMessage && typeof b.lastMessage.timestampNum === 'number') ? b.lastMessage.timestampNum : toMs(b.lastMessage?.timestamp || b.lastMessage?.date);
          // dateCreated first — same correction as the `index` composition above. These two MUST
          // agree: `index` is what vue-advanced-chat sorts by, this comparator is what orders the
          // array we hand it. Fixing only one makes a room's position depend on which ran last.
          const aT = Math.max(aLm || 0, toMs(a.dateUpdated) || 0, toMs(a.dateCreated || a.createdAt || a.createdOn) || 0);
          const bT = Math.max(bLm || 0, toMs(b.dateUpdated) || 0, toMs(b.dateCreated || b.createdAt || b.createdOn) || 0);
          return bT - aT;
        });

        // Merge any localRooms not present in snapshot so newly created (message-less) conversations still show
        try {
          const localList = Object.values(this.state.conversationsV2.localRooms || {});
          localList.forEach(localRoom => {
            const exists = rooms.findIndex(r => r.roomId === localRoom.roomId) !== -1;
            if (!exists) {
              rooms.unshift(localRoom);
            } else {
              // If it now exists in snapshot, drop the local copy
              try { this.commit('conversationsV2/removeLocalRoom', localRoom.roomId); } catch(_) {}
            }
          });
        } catch(e) {}
        // Preserve local room previews when they are newer than what Firestore provides.
        // This handles the race between sendMessage writing to the subcollection (which triggers
        // updateRoomLastMessageLocally via the messages onSnapshot) and the parent doc update
        // (which triggers this rooms onSnapshot). The subcollection write happens first, so the
        // local preview from messages listener may be ahead of the parent doc snapshot.
        try {
          const existing = (this.state.conversationsV2.rooms || []).reduce((acc, r) => { acc[r.roomId] = r; return acc; }, {});
          for (let i = 0; i < rooms.length; i += 1) {
            const r = rooms[i];
            const old = existing[r.roomId];
            if (old) {
              const newPreview = r.lastMessagePreview || '';
              const oldPreview = old.lastMessagePreview || '';
              const newTs = r.lastMessage?.timestampNum || 0;
              const oldTs = old.lastMessage?.timestampNum || 0;
              // Case 1: Firestore preview is empty but local has content → keep local
              // Case 2: Local preview is newer (higher timestamp) → keep local
              if ((!newPreview && oldPreview) || (oldPreview && oldTs > newTs)) {
                r.lastMessagePreview = oldPreview;
                if (!r.lastMessage) r.lastMessage = {};
                if (typeof r.lastMessage.content !== 'string' || !r.lastMessage.content || oldTs > newTs) {
                  r.lastMessage.content = oldPreview;
                }
                // Preserve the newer timestamp so sorting stays correct
                if (oldTs > newTs) {
                  r.lastMessage.timestampNum = oldTs;
                  r.lastMessage.date = oldTs;
                  r.index = Math.max(r.index || 0, oldTs);
                }
              }
            }
          }
        } catch(_) {}
        commit('setRooms', rooms);
        commit('setLoadingRooms', false);
        commit('setRoomsLoaded', true);

        // Detect new inbound SMS messages to trigger sound + push notifications.
        // Pure detection lives in utils/smsNotificationDetection (tested in
        // cypress/node-tests/sms-notification-detection.test.js). Only fires
        // after the first snapshot so historical unread doesn't ping.
        try {
          const isFirst = this.state.conversationsV2._isFirstRoomsSnapshot;
          const prevTsMap = this.state.conversationsV2._prevRoomLastMsgTs || {};
          const { nextPrevTsMap, notification } = detectInboundSMSNotification({
            rooms,
            prevTsMap,
            isFirstSnapshot: isFirst,
            currentUserID: this.state.customer?.userID || null,
            currentUsername: this.state.customer?.user?.Name || null,
          });

          // Persist per-room timestamp advances so future snapshots have correct baseline.
          Object.keys(nextPrevTsMap).forEach(roomId => {
            if (nextPrevTsMap[roomId] !== (prevTsMap[roomId] || 0)) {
              commit('setPrevRoomLastMsgTs', { roomId, ts: nextPrevTsMap[roomId] });
            }
          });

          if (notification) {
            commit('setLatestInboundMessage', notification);
            commit('setNewConversationMsgTimestamp', Date.now());
          }

          if (isFirst) {
            commit('setIsFirstRoomsSnapshot', false);
          }
        } catch (e) {
          try { console.warn('[ConversationsV2] SMS notification detection failed:', e); } catch(_) {}
        }

        // Update sidebar notification based on unread status
        dispatch('updateSidebarNotification');

        // REMOVED (Apr 2026): refreshRoomTimestampsFromMessages call was here on first snapshot.
        // Removed because it overwrote correct parent-doc timestamps with stale subcollection
        // data due to Firestore mixed-type ordering (String vs Timestamp dateCreated).
        // NOTE (Jan 22, 2026): Removed per-room preview listeners. The main query onSnapshot already
        // handles real-time updates when documents change. The old preview listener code was broken
        // (calling removed mutations) and creating memory leaks (orphaned listeners).
      }, {
        source: 'conversations/rooms',
        onError: (error) => {
          try { console.error('[ConversationsV2] onSnapshot error:', error); } catch (_) {}
          commit('setLoadingRooms', false);
          commit('setRoomsLoaded', true);
        },
      });
      commit('setRoomsListener', unsubscribe);
      commit('customer/addFirestoreListener', unsubscribe, { root: true });
      // NOTE (Jan 22, 2026): Removed duplicate per-room listener block that was causing memory leaks
      // and incorrect timestamp behavior. The first block (lines 608-647) properly manages listeners.
    },
    addLocalRoom({ commit, rootState }, room) {
      // Normalize lastMessage timestamp display for consistency with rooms rendered from Firestore
      try {
        // Settings live at customer.settings (array); there is no customer.allSettings.
        const tz = (rootState.customer?.settings || []).find(s => s.Property === 'GENERAL_TIMEZONE')?.PropertyValue || 'America/New_York';
        const ts = room?.lastMessage?.timestampNum || room?.lastMessage?.date || Date.now();
        const dateObj = new Date(typeof ts === 'number' ? ts : (new Date(ts)).getTime());
        if (!room.lastMessage) room.lastMessage = {};
        room.lastMessage.timestamp = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(dateObj).replace(',', '');
        // CRITICAL: Set index for vue-advanced-chat sorting (higher = first)
        room.index = ts;
      } catch(_) {}
      commit('setLocalRoom', room);
      // Immediately show locally so user sees new convo without waiting for Firestore
      commit('upsertRoomToVisibleList', room);
      commit('setRoomsLoaded', true);
      commit('setLoadingRooms', false);
    },
    removeLocalRoom({ commit }, roomId) {
      commit('removeLocalRoom', roomId);
    },
    // Deep link (?roomId=) to a thread outside the loaded room window: fetch that one doc and show it as
    // a local room, which the rooms listener keeps until the real room arrives. Returns the room, or null
    // when it doesn't exist or belongs to another site. See src/utils/conversationDeepLink.js.
    async fetchRoomById({ dispatch, rootState, state }, { roomId, siteID }) {
      if (!roomId || !siteID) return null;
      const existing = (state.rooms || []).find(r => r && r.roomId === roomId);
      if (existing) return existing;
      const { db, doc: docRef, getDoc } = rootState;
      if (!db || !docRef || !getDoc) return null;
      const snap = await getDoc(docRef(db, 'conversations', roomId));
      if (!snap || !snap.exists()) return null;
      const room = buildDeepLinkedRoom(roomId, snap.data(), { siteID });
      if (!room) return null;
      await dispatch('addLocalRoom', room);
      return (state.rooms || []).find(r => r && r.roomId === roomId) || room;
    },
    async fetchMessages({ commit, rootState, dispatch, state }, { roomId, messagesPerPage = 50 }) {
      // FIX (Jan 23, 2026): Skip if already loading messages for this room
      // This prevents duplicate fetches when room is selected multiple times
      if (state._fetchingMessagesForRoom === roomId) {
        return;
      }
      state._fetchingMessagesForRoom = roomId;
      const { collection, query, orderBy, limit, getDocs, db } = rootState;
      commit('setMessages', []);
      commit('setMessagesLoaded', false);
      const coll = collection(db, 'conversations', roomId, 'messages');
      const buildQuery = (field, direction = 'desc') => query(
        coll,
        orderBy(field, direction),
        limit(messagesPerPage)
      );
      let snap;
      try {
        // Try common field names in order
        const fieldsToTry = ['dateCreated', 'timestamp', 'date'];
        for (let i = 0; i < fieldsToTry.length; i += 1) {
          const field = fieldsToTry[i];
          try {
            const q = buildQuery(field, 'desc');
            const s = await getDocs(q);
            if (!s.empty) { snap = s; break; }
          } catch (e) {}
        }
        // Last resort: un-ordered fetch
        if (!snap || snap.empty) {
          const q = limit ? query(coll, limit(messagesPerPage)) : coll;
          snap = await getDocs(q);
        }
      } catch (err) {
        console.error('[ConversationsV2] fetchMessages error:', err);
        snap = { empty: true, forEach: () => {} };
      }
      // NOT converged onto @/utils/firestoreTime — returns a Date, not epoch millis. Could be
      // expressed as `new Date(coerceToMs(v))`, but that is a shape refactor of the message
      // pipeline rather than de-duplication, so it is out of scope for this pass.
      const coerceDate = (v) => {
        try {
          if (!v && v !== 0) return null;
          if (typeof v?.toDate === 'function') return v.toDate();
          if (typeof v === 'object' && typeof v._seconds === 'number') return new Date(v._seconds * 1000);
          const d = new Date(v);
          return isNaN(d.getTime()) ? null : d;
        } catch (_) { return null; }
      };
      // Settings live at customer.settings (array); there is no customer.allSettings.
      const siteTz = (rootState.customer?.settings || []).find(s => s.Property === 'GENERAL_TIMEZONE')?.PropertyValue || 'America/New_York';
      let messages = [];
      snap.forEach(docSnap => {
        const m = docSnap.data() || {};
        const attrs = normalizeMessageAttributes(m.attributes);
        // Safety net: hide tapback reaction messages (they're iOS/Android reaction fallbacks)
        if (attrs?._tapback === true) return;
        const dateObj = coerceDate(m.dateCreated || m.timestamp || m.date);
        const tsNum = dateObj ? dateObj.getTime() : 0;
        const status = attrs?.status;
        const isOutbound = attrs?.outbound === true || attrs?.sentViaAPI === true || m.isOutbound === true;
        const mappedSenderId = isOutbound
          ? (rootState.customer?.siteID || rootState.customer?.userID || rootState.customer?.user?.EmployeeID || 'current')
          : (m.author || 'patient');  // Use author (usually phone number) for patient messages
        messages.push({
          ...m,
          _id: docSnap.id,
          sender_id: mappedSenderId,
          senderId: mappedSenderId,
          username: m.username || m.author || mappedSenderId || 'User',
          content: (function(){
            const txt = m.content || m.body || '';
            // FIXED: Never overwrite message content - trust backend data
            return txt;
          })(),
          // Match Chat pattern: timestamp shown on bubble; date shown as section header string
          timestamp: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: siteTz }).format(dateObj).replace(',', '') : '',
          date: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: siteTz }).format(dateObj) : '',
          saved: status === 'sent' || status === 'delivered' || true,
          distributed: m.distributed === true || status === 'delivered' || false,
          tsNum,
          attributes: attrs
        });
      });
      // Sort messages ascending by numeric timestamp
      messages.sort((a, b) => (a.tsNum || 0) - (b.tsNum || 0));
      // Fallback: if still empty, try an unbounded fetch and rebuild
      if (messages.length === 0) {
        try {
          const allSnap = await getDocs(coll);
          const tmp = [];
          allSnap.forEach(docSnap => {
            const m = docSnap.data() || {};
            const attrs = normalizeMessageAttributes(m.attributes);
            // Safety net: hide tapback reaction messages (they're iOS/Android reaction fallbacks)
            if (attrs?._tapback === true) return;
            const dateObj = coerceDate(m.dateCreated || m.timestamp || m.date);
            const status = attrs?.status;
            const isOutbound = attrs?.outbound === true || attrs?.sentViaAPI === true || m.isOutbound === true;
            const mappedSenderId = isOutbound
              ? (rootState.customer?.siteID || rootState.customer?.userID || rootState.customer?.user?.EmployeeID || 'current')
              : (m.author || 'patient');  // Use author (usually phone number) for patient messages
            tmp.push({
              ...m,
              _id: docSnap.id,
              sender_id: mappedSenderId,
              senderId: mappedSenderId,
              content: m.content || m.body || '',
              timestamp: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: siteTz }).format(dateObj).replace(',', '') : '',
              date: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: siteTz }).format(dateObj) : '',
              saved: status === 'sent' || status === 'delivered' || true,
              distributed: m.distributed === true || status === 'delivered' || false,
              attributes: attrs,
              tsNum: dateObj ? dateObj.getTime() : 0
            });
          });
          tmp.sort((a, b) => {
            // NOT converged onto @/utils/firestoreTime — deliberately, and this is the important
            // one. It has NO Timestamp/_seconds handling and is fed `a.date || a.timestamp`, which
            // at this point are the DISPLAY STRINGS this file generated (e.g. "July 13, 2026"),
            // not raw Firestore values. Swapping in the shared coercer would make it start
            // resolving Timestamps it currently collapses to 0 — a behaviour change to message
            // ordering. Left as-is on purpose. (2026-07-21 convergence pass.)
            const toMs = (v) => { const d = new Date(v); const ms = d.getTime(); return isNaN(ms) ? 0 : ms; };
            const am = toMs(a.date || a.timestamp) || (typeof a.index === 'number' ? a.index : 0);
            const bm = toMs(b.date || b.timestamp) || (typeof b.index === 'number' ? b.index : 0);
            return am - bm;
          });
          messages = tmp;
        } catch(_) {}
      }
      try {
        // Normalization, the /sms-media re-sign route, the unservable-media placeholder AND the
        // multi-photo fan-out all live in ONE shared primitive (utils/legacyMedia.expandMediaMessage)
        // so this path and the live listener below cannot drift — near-identical copies of this
        // mapping are exactly how divergent media bugs ship (Media-URL Standard, rule 3).
        const apiBase = (axios && axios.defaults && axios.defaults.baseURL) || '';
        const expanded = [];
        messages.forEach(msg => { expandMediaMessage(msg, apiBase).forEach(m => expanded.push(tagSimulatedSend(m))); });
        messages = expanded;
      } catch (_) {}
      commit('setMessages', messages);
      commit('setMessagesLoaded', true);
      // Phase D: removed the /message-media proxy-URL synthesis for legacy Twilio media.
      // ConversationContainerV2 renders a placeholder for mediaSid-without-S3-url messages.
      // REMOVED: Don't update room preview when just viewing existing messages
      // The room's lastMessage preview was already set when rooms loaded from Firestore.
      // Calling updateRoomLastMessageLocally here causes unnecessary re-renders/flickering
      // because it triggers upsertRoomToVisibleList which creates a new rooms array.
      // The live listener (below) will update the preview when NEW messages arrive.
      // Detach previous message listener to avoid cross-room updates
      // 🔧 LAYER 1 FIX: Always unsubscribe old listener before attaching new one
      // This prevents multiple listeners on the same room causing duplicate messages
          try {
            if (state.messageListener) {
              // Always unsubscribe the old listener before attaching a new one
              // This prevents listener accumulation when fetchMessages() is called multiple times on the same room
              state.messageListener();
              commit('setMessageListener', null);
            }
          } catch(_) {}
      // Live listener for new messages — prefer 'dateCreated', then 'timestamp', then fallback
      // 🔧 LAYER 3 FIX: Add limit to match initial load (messagesPerPage) to prevent query mismatch
      // This ensures the listener doesn't load ALL messages while initial load only gets last 50
      try {
        const coll = collection(db, 'conversations', roomId, 'messages');
        let liveQ;
        try {
          // Use descending order so limit() returns the newest documents, then sort ascending client-side for display
          liveQ = query(coll, orderBy('dateCreated', 'desc'), limit(messagesPerPage));
        } catch (e1) {
          try { liveQ = query(coll, orderBy('timestamp', 'desc'), limit(messagesPerPage)); } catch (e2) {
            // As a last resort, apply limit without ordering (Firestore will use __name__) — messages may appear unordered
            liveQ = query(coll, limit(messagesPerPage));
          }
        }
          const unsub = safeSnapshot(liveQ, (liveSnap) => {
          const prevMsgCount = state.messages?.length || 0;
          const isNewIncoming = prevMsgCount > 0 && liveSnap.size > prevMsgCount;
          // Guard: ignore updates if user switched rooms meanwhile
          if (state.selectedRoomId !== roomId) return;
          const liveMsgs = [];
          liveSnap.forEach(docSnap => {
            const m = docSnap.data() || {};
            const attrs = normalizeMessageAttributes(m.attributes);
            // Safety net: hide tapback reaction messages (they're iOS/Android reaction fallbacks)
            if (attrs?._tapback === true) return;
            const dateObj = coerceDate(m.dateCreated || m.timestamp || m.date);
            const tsNum = dateObj ? dateObj.getTime() : 0;
            const status = attrs?.status;
            const isOutbound = attrs?.outbound === true || attrs?.sentViaAPI === true;
            // 🔧 FIX: Use siteID for consistent alignment across all site employees
            const mappedSenderId = isOutbound
              ? (rootState.customer?.siteID || rootState.customer?.userID || rootState.customer?.user?.EmployeeID || 'current')
              : (m.sender_id || m.senderId || m.author);
            liveMsgs.push({
              ...m,
              _id: docSnap.id,
              sender_id: mappedSenderId,
              senderId: mappedSenderId,
              username: m.username || m.author || mappedSenderId || 'User',
              content: m.content || m.body || '',
              timestamp: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: siteTz }).format(dateObj).replace(',', '') : '',
              date: dateObj ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: siteTz }).format(dateObj) : '',
              saved: status === 'sent' || status === 'delivered' || true,
              distributed: m.distributed === true || status === 'delivered' || false,
              tsNum
            });
          });
          // Ensure ascending order
          liveMsgs.sort((a, b) => (a.tsNum || 0) - (b.tsNum || 0));
          try {
            // SAME shared primitive as the initial-load path above — normalization, the /sms-media
            // re-sign route, the placeholder and the multi-photo fan-out. Do not inline a copy here.
            const liveApiBase = (axios && axios.defaults && axios.defaults.baseURL) || '';
            const enriched = [];
            liveMsgs.forEach(msg => { expandMediaMessage(msg, liveApiBase).forEach(m => enriched.push(tagSimulatedSend(m))); });
            // 🔧 SIMPLIFIED: Firestore snapshot is the source of truth
            // Keep any optimistic messages that haven't been confirmed yet, then add Firestore messages
            const optimisticMessages = (state.messages || []).filter(m => {
              const id = m._id || m.id || '';
              const attrs = normalizeMessageAttributes(m.attributes);
              return id.startsWith('temp_') || attrs?.optimistic === true;
            });
            
            // Continuation bubbles are DISPLAY-only clones of a real message (multi-photo fan-out),
            // so they must not take part in optimistic dedupe: their blank content synthesises a
            // `ts::sender` key that could suppress a genuine pending message.
            const realOnly = enriched.filter(msg => !msg.isMediaContinuation);
            const realClientNonces = new Set(realOnly.map(msg => getClientNonce(msg)).filter(Boolean));

            // ENHANCED DEDUPE: Also dedupe by content+timestamp+sender for messages without nonce
            const realMessageKeys = new Set(realOnly.map(msg => {
              const content = (msg.content || msg.body || '').trim();
              const ts = msg.tsNum || 0;
              const sender = msg.sender_id || msg.senderId || '';
              return `${ts}:${content}:${sender}`;
            }));
            
            const filteredOptimistic = optimisticMessages.filter(msg => {
              const nonce = getClientNonce(msg);
              if (nonce && realClientNonces.has(nonce)) {
                return false;
              }
              // Fallback: match by content+timestamp+sender
              const content = (msg.content || msg.body || '').trim();
              const ts = msg.tsNum || 0;
              const sender = msg.sender_id || msg.senderId || '';
              const key = `${ts}:${content}:${sender}`;
              if (realMessageKeys.has(key)) {
                return false;
              }
              return true;
            });
            // Use Firestore messages as base, add any pending optimistic messages that have not been confirmed yet
            const mergedMessages = [...enriched, ...filteredOptimistic];
            mergedMessages.sort((a, b) => (a.tsNum || 0) - (b.tsNum || 0));
            commit('setMessages', mergedMessages);
            // Phase D: removed the /message-media proxy-URL synthesis (legacy Twilio media → placeholder).
          } catch(_) { commit('setMessages', liveMsgs); }
          commit('setMessagesLoaded', true);
          // Update left-pane lastMessage preview from latest message in snapshot
          // FIX (Mar 13, 2026): Removed isNewIncoming guard — optimistic messages can inflate
          // prevMsgCount, causing liveSnap.size <= prevMsgCount even when a real new message arrives.
          // The setRooms debounce (content-aware) now prevents unnecessary re-renders.
          try {
            const latest = liveMsgs.length ? liveMsgs[liveMsgs.length - 1] : null;
            if (latest) {
              dispatch('updateRoomLastMessageLocally', { roomId, content: latest.content || '', ts: latest.tsNum || Date.now() });
            }
          } catch(_) {}
          // Preview refresh disabled; proxy URLs are already persisted in Firestore
          try {} catch(_) {}
        }, { source: 'conversations/messages' });
        commit('setMessageListener', unsub);
      } catch(_) {}
      // Clear the fetching flag when done
      state._fetchingMessagesForRoom = null;
    },
    // REMOVED: refreshMessagePreview - No longer needed after backend fix
    // The backend now provides correct message content and file URLs from the start
    detachAll({ state, commit }) {
      try { if (state.roomsListener) state.roomsListener(); } catch (_) {}
      try { if (state.messageListener) state.messageListener(); } catch (_) {}
      state.listeners.forEach(l => { try { l(); } catch (_) {} });
      commit('setRoomsListener', null);
      commit('setMessageListener', null);
      commit('setListeners', []);
    },
    // Detach only page-specific listeners when navigating away from the conversations page.
    // Does NOT touch roomsListener — that is owned by Layout.vue so the sidebar keeps
    // receiving real-time unread updates even when the user is on a different page.
    // detachAll() is still used on logout and full app teardown.
    detachPageListeners({ state, commit }) {
      try { if (state.messageListener) state.messageListener(); } catch (_) {}
      state.listeners.forEach(l => { try { l(); } catch (_) {} });
      commit('setMessageListener', null);
      commit('setListeners', []);
    },
    
    // Patient SMS Widget Actions
    resetPatientSms({ commit }) {
      // Clear all patient SMS state to prevent cross-patient contamination
      commit('setPatientSmsMode', false);
      commit('setPatientSmsWidgetOpen', false);
      commit('setPatientSmsWidgetMinimized', false);
      commit('setCurrentPatientForSms', null);
      commit('setPatientSmsConversation', null);
      commit('setPatientSmsMessages', []);
      commit('setPatientSmsMessagesLoaded', false);
      commit('setPatientSmsListener', null);
      
      // Also clear the main conversation state to prevent showing wrong messages
      commit('setSelectedRoomId', null);
      commit('setMessages', []);
      commit('setMessagesLoaded', false);
    },
    
    async initPatientSms({ commit, dispatch, rootState }, { patient, siteId }) {
      try {
        // Reset any previous patient state first
        await dispatch('resetPatientSms');
        
        commit('setPatientSmsMode', true);
        commit('setCurrentPatientForSms', patient);
        
        // Check if patient has existing SMS conversation
        const existingConversation = await dispatch('findPatientSmsConversation', { 
          patientPhone: patient.WirelessPhone,
          siteId 
        });
        
        if (existingConversation) {
          commit('setPatientSmsConversation', existingConversation);
          await dispatch('loadPatientSmsMessages', existingConversation.roomId);
        }
      } catch (error) {
        console.error('[PatientSMS] Init error:', error);
        throw error;
      }
    },
    
    async findPatientSmsConversation({ rootState }, { patientPhone, siteId }) {
      try {
        if (!patientPhone) return null;
        // The backend createConversation handles finding existing conversations via unique_name lookup
        return null;
      } catch (error) {
        console.error('[PatientSMS] Find conversation error:', error);
        return null;
      }
    },
    
    async createPatientSmsConversation({ commit, dispatch, rootState }, { patient, siteId }) {
      try {
        if (!patient.WirelessPhone) {
          throw new Error('Patient does not have a wireless phone number');
        }
        
        // Get site information for the payload
        const siteName = rootState.customer?.sites?.find(s => s.SiteID === siteId)?.Name || 'TeamIO Practice';
        
        // Transform settings array to keyed object (like the mixin's allSettings computed property)
        const settings = rootState.customer?.settings || [];
        const allSettings = settings.length > 0 ? _.keyBy(settings, 'Property') : {};

        // Check if GENERAL_PHONE exists and has a valid value (not empty after trimming)
        if (!allSettings?.GENERAL_PHONE || 
            !allSettings.GENERAL_PHONE.PropertyValue || 
            allSettings.GENERAL_PHONE.PropertyValue.trim() === '') {
          throw new Error('No valid Twilio phone number found for this site. GENERAL_PHONE setting not found or empty.');
        }
        
        const sitePhone = allSettings.GENERAL_PHONE.PropertyValue.trim();

        // Format phone number to E.164 format
        let phoneDigits = patient.WirelessPhone.replace(/\D/g, '');
        
        // Handle different phone number formats
        if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) {
          // Remove leading 1 for US numbers
          phoneDigits = phoneDigits.substring(1);
        }
        
        if (phoneDigits.length !== 10) {
          throw new Error(`Invalid phone number format: ${patient.WirelessPhone}. Expected 10 digits.`);
        }
        
        const e164Phone = `+1${phoneDigits}`;

        // Use existing createConversation endpoint with correct payload format
        const payload = {
          SiteID: siteId,
          SiteName: siteName,
          SitePhone: sitePhone,
          source: 'patient_sms_widget', // Feature flag for phone-based search
          Users: [{
            PatNum: patient.PatNum,
            WirelessPhone: e164Phone,
            Patient: patient.PatientName || patient.Name, // Backend expects 'Patient' field
            FName: patient.FName || patient.PatientName?.split(' ')[0] || patient.Name?.split(' ')[0] || '',
            LName: patient.LName || patient.PatientName?.split(' ')[1] || patient.Name?.split(' ')[1] || ''
          }]
        };
        
        const response = await axios.post('/create-conversation', payload);
        const conversationData = response.data;

        // Handle both new conversation (conversationSID) and existing conversation (sid) formats
        const conversationSID = conversationData?.conversationSID || conversationData?.sid;
        
        if (conversationData && conversationSID) {
          const conversation = {
            roomId: conversationSID,
            conversationSID: conversationSID,
            patientName: patient.PatientName || patient.Name,
            patientPhone: e164Phone,
            status: 'active',
            unreadCount: 0
          };
          
          commit('setPatientSmsConversation', conversation);
          commit('setPatientSmsMessages', []);
          commit('setPatientSmsMessagesLoaded', true);

          return conversation;
        }
        
        throw new Error('Failed to create conversation - no conversationSID returned');
      } catch (error) {
        console.error('[PatientSMS] Create conversation error:', error);
        throw error;
      }
    },
    
    async loadPatientSmsMessages({ commit, state }, conversationId) {
      try {
        commit('setPatientSmsMessagesLoaded', false);
        
        const payload = {
          action: 'GET_CONVERSATION_MESSAGES',
          conversationSID: conversationId
        };
        
        const response = await axios.post('/runquery', payload);
        const messages = response.data?.messages || [];
        
        commit('setPatientSmsMessages', messages);
        commit('setPatientSmsMessagesLoaded', true);
        
        return messages;
      } catch (error) {
        console.error('[PatientSMS] Load messages error:', error);
        commit('setPatientSmsMessagesLoaded', true);
        throw error;
      }
    },
    
    async sendPatientSmsMessage({ commit, state, rootState, dispatch, getters }, { content, conversationSID, file }) {
      // File validation and processing (same as main Conversations page)
      if (file && (file.size || file.type)) {
        const fileSizeMB = file.size / (1024 * 1024);
        const fileName = file.name || 'Unknown file';
        const fileType = (file.type || '').toLowerCase();
        
        // SMS file size limit (5MB for SMS, 16MB for WhatsApp, 150MB for chat)
        const smsFileSizeLimit = 5; // MB
        if (fileSizeMB > smsFileSizeLimit) {
          throw new Error(`File size (${fileSizeMB.toFixed(1)}MB) exceeds SMS limit of ${smsFileSizeLimit}MB. Please choose a smaller file.`);
        }
        
        // Define SMS-supported file types (stricter subset)
        const smsDeliverableTypes = ['image/jpeg', 'image/jpg', 'image/png', 'audio/mp3', 'audio/amr', 'video/mp4', 'application/pdf'];
        const isSmsSupported = smsDeliverableTypes.some(type => 
          fileType.includes(type) || fileType === type || 
          (type.includes('jpeg') && fileType.includes('jpg')) ||
          (type.includes('jpg') && fileType.includes('jpeg'))
        );
        
        if (!isSmsSupported) {
          throw new Error(`File type "${fileType}" is not supported for SMS. Supported types: Images (JPG, PNG), Audio (MP3, AMR), Video (MP4), PDF.`);
        }
      }
      // Add optimistic message immediately for instant feedback
      const optimisticMessage = {
        _id: `temp_${Date.now()}`,
        content: content,
        body: content,
        text: content,
        dateCreated: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        date: new Date().toISOString(),
        senderId: rootState.customer?.userID || 'site',
        sender_id: rootState.customer?.userID || 'site',
        username: rootState.customer?.user?.FName + ' ' + rootState.customer?.user?.LName || 'You',
        saved: false, // Will be true once confirmed by backend
        distributed: false,
        seen: false,
        attributes: {
          sentViaAPI: true,
          outbound: true,
          optimistic: true // Mark as optimistic
        }
      };
      
      // Add to main messages array for immediate display
      commit('addMessage', optimisticMessage);

      try {
        // Use existing sendMessage endpoint with correct payload format
        const payload = {
          roomId: conversationSID,
          message: {
            content: content
          },
          backgroundTaskPayload: {
            siteID: rootState.customer?.siteID
          }
        };
        
        // Add file to payload if present (same as main Conversations page)
        if (file) {
          payload.message.file = {
            name: file.name,
            size: file.size,
            type: file.type,
            url: file.localUrl || file.url || ''
          };
          
          // Convert file to base64 for backend upload (same as main Conversations page)
          try {
            let sourceBlob = file.blob || null;
            if (!sourceBlob && payload.message.file.url && /^blob:\/\//.test(payload.message.file.url)) {
              const resp = await fetch(payload.message.file.url);
              sourceBlob = await resp.blob();
            }
            if (sourceBlob) {
              const dataUrl = await blobToDataUrl(sourceBlob, file.type);
              if (dataUrl) {
                payload.message.file.base64 = dataUrl;
              }
            }
          } catch (e) {}
        }

        const response = await axios.post('/send-message', payload);
        const messageData = response.data;
        
        if (messageData && messageData.success) {
          // Update the optimistic message to show it was sent successfully
          const confirmedMessage = {
            saved: true,
            distributed: true,
            attributes: {
              ...optimisticMessage.attributes,
              optimistic: false,
              confirmed: true
            }
          };
          
          // Replace optimistic message with confirmed one
          commit('updateMessage', { messageId: optimisticMessage._id, message: confirmedMessage });
          return optimisticMessage;
        }

        // Expected, TERMINAL non-delivery (opt-out / invalid / undeliverable number) now
        // arrives as HTTP 200 { success:false, error }. Mark the optimistic bubble failed
        // WITH the code so the panel shows the reason and hides retry (isOptedOutError).
        // Do NOT throw — the catch below would overwrite failedCode with UNKNOWN_ERROR.
        const nd = classifyNonDelivery(messageData);
        if (nd.isNonDelivery) {
          commit('updateMessage', {
            messageId: optimisticMessage._id,
            message: {
              saved: false,
              attributes: {
                ...optimisticMessage.attributes,
                optimistic: false,
                failed: true,
                failedReason: nd.message,
                failedCode: nd.code,
              },
            },
          });
          return optimisticMessage;
        }

        throw new Error('Failed to send message');
      } catch (error) {
        console.error('[PatientSMS] Send message error:', error);
        
        // Check if this is a permanently closed conversation error (410 Gone)
        if (error.response?.status === 410 && error.response?.data?.error === 'CONVERSATION_PERMANENTLY_CLOSED') {
          // CRITICAL: Prevent infinite loop by checking retry attempts
          // This happens when backend returns the same closed conversation repeatedly
          if (conversationSID && state.patientSmsConversation?.roomId === conversationSID) {
            // We already tried with this conversation and got 410, don't retry
            console.error('[PatientSMS] Backend keeps returning the same closed conversation. Stopping retry loop.');
            commit('removeMessage', optimisticMessage._id);
            alert('Unable to send message. This conversation is permanently closed. Please close and reopen the SMS window.');
            throw new Error('Conversation is permanently closed. Please close and reopen the SMS window.');
          }
          
          console.warn('[PatientSMS] Conversation is permanently closed, creating new conversation and retrying...');
          
          // Remove the failed optimistic message
          commit('removeMessage', optimisticMessage._id);
          
          // Clear the current conversation
          commit('setPatientSmsConversation', null);
          
          // Create a new conversation
          const patientData = rootState.conversationsV2.currentPatientForSms;
          if (!patientData || !patientData.WirelessPhone) {
            throw new Error('Cannot create new conversation: patient data not available');
          }
          
          // Call createPatientSmsConversation to create a fresh conversation
          await dispatch('createPatientSmsConversation', {
            patient: patientData,
            siteId: rootState.customer.siteID
          });
          
          // Retry sending the message with the new conversation
          const newConversation = getters.currentPatientSmsConversation;
          if (newConversation && newConversation.roomId) {
            // CRITICAL CHECK: If backend returned the same conversation ID, stop the loop
            if (newConversation.roomId === conversationSID) {
              alert('Unable to create a new conversation. Please close and reopen the SMS window.');
              throw new Error('Backend returned the same closed conversation. Please close and reopen the SMS window.');
            }

            return await dispatch('sendPatientSmsMessage', { 
              content, 
              conversationSID: newConversation.roomId, // Pass the new conversation ID
              file 
            });
          } else {
            throw new Error('Failed to create new conversation');
          }
        }
        
        // Mark optimistic message as failed for other errors
        // ENHANCED: Capture the specific error reason for display in UI
        const errorReason = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send message';
        const errorCode = error.response?.data?.error || 'UNKNOWN_ERROR';
        
        commit('updateMessage', { 
          messageId: optimisticMessage._id, 
          message: { 
            saved: false,
            attributes: {
              ...optimisticMessage.attributes,
              optimistic: false,
              failed: true,
              failedReason: errorReason,
              failedCode: errorCode
            }
          }
        });
        
        throw error;
      }
    },
    
    async sendWikiLink({ commit, rootState }, { wikiId, wikiName, wikiContent, editedContent, patientId, conversationId }) {
      try {
        // Use the same endpoint as existing PatientSidebar functionality
        const siteInfo = rootState.customer?.sites?.find(s => s.SiteID === rootState.customer.siteID);
        const patientData = rootState.conversationsV2.currentPatientForSms || rootState.customer?.patientData;
        
        // Show the edited content (or original template) with processing indicator
        let templatePreview = editedContent || wikiContent || (wikiName ? `Template: ${wikiName}` : 'SMS Template');
        templatePreview = `${templatePreview}\n\n(Processing template variables...)`;
        
        // Add optimistic message immediately for instant feedback
        const optimisticMessage = {
          _id: `temp_wiki_${Date.now()}`,
          content: templatePreview,
          body: templatePreview,
          text: templatePreview,
          dateCreated: new Date().toISOString(),
          tsNum: Date.now(),
          timestamp: Date.now(),
          author: rootState.customer?.user?.Name || 'Site',
          senderId: 'site',
          sender_id: 'site',
          saved: false,
          distributed: false,
          seen: false,
          attributes: {
            sentViaAPI: true,
            outbound: true,
            optimistic: true, // Mark as optimistic
            messageType: 'wiki_template',
            wikiId: wikiId
          }
        };
        
        // Add to main messages array for immediate display
        commit('addMessage', optimisticMessage);

        const payload = {
          siteID: rootState.customer?.siteID,
          patNum: patientId,
          smsTemplateID: wikiId,
          defaultDB: siteInfo?.DefaultDB,
          userODNum: rootState.customer?.user?.ODUserNum,
          employeeName: rootState.customer?.user?.Name,
          // Pass edited text so backend uses it instead of re-fetching wiki content
          // If user didn't edit, editedContent matches original — backend still runs variable resolution
          ...(editedContent ? { smsText: editedContent } : {})
        };

        console.log('[PatientSMS] Sending wiki template via /send-text-msg:', payload);
        const response = await axios.post('/send-text-msg', payload);

        // A 200 { success:false } body is a REFUSAL (opt-out / undeliverable), NOT a send —
        // a successful send returns the bare string "success". Throw a tagged Error carrying
        // the human reason so the catch marks the optimistic message failed and the component
        // surfaces the reason instead of a false "sent".
        if (response && response.data && response.data.success === false) {
          const optOut = optOutResultFromSendFailure(response.data);
          const refusal = new Error((optOut && optOut.reason) || response.data.message || 'The text message was not sent.');
          refusal.isSendRefusal = true;
          throw refusal;
        }
        console.log('[PatientSMS] Wiki template sent successfully, response:', response.data);

        // Remove the optimistic message after successful send
        // The real message will arrive via Firestore listener with actual content
        console.log('[PatientSMS] Removing optimistic message:', optimisticMessage._id);
        commit('removeMessage', optimisticMessage._id);

        return response.data;
      } catch (error) {
        console.error('[PatientSMS] Send wiki template error:', error);
        
        // Mark optimistic message as failed
        commit('updateMessage', { 
          messageId: optimisticMessage._id, 
          message: { 
            saved: false,
            attributes: {
              ...optimisticMessage.attributes,
              optimistic: false,
              failed: true
            }
          }
        });
        
        throw error;
      }
    },
    
    async sendFormLink({ commit, rootState }, { formId, formName, patientId, conversationId }) {
      let optimisticMessage = null; // Declare outside try block for catch block access
      try {
        // Get site info for message formatting
        const siteInfo = rootState.customer?.sites?.find(s => s.SiteID === rootState.customer.siteID);
        const patientData = rootState.conversationsV2.currentPatientForSms || rootState.customer?.patientData;
        
        if (!siteInfo || !patientData) {
          console.error('[PatientSMS] Missing data - siteInfo:', siteInfo, 'patientData:', patientData);
          throw new Error('Site or patient information not available');
        }
        
        // Format SMS message with form link (same as PatientSidebar)
        const displayName = formName || 'Form';
        const smsMessage = `${siteInfo.Name}: Hello ${patientData.FName}, Please fill out the following form: \n\n<a href="{{patient_link_for_form_${formId}}}">${displayName}</a>`;
        
        // Add optimistic message immediately for instant feedback
        optimisticMessage = {
          _id: `temp_form_${Date.now()}`,
          content: smsMessage,
          body: smsMessage,
          text: smsMessage,
          dateCreated: new Date().toISOString(),
          tsNum: Date.now(),
          timestamp: Date.now(),
          author: rootState.customer?.user?.Name || 'Site',
          senderId: 'site',
          sender_id: 'site',
          saved: false,
          distributed: false,
          seen: false,
          attributes: {
            sentViaAPI: true,
            outbound: true,
            optimistic: true, // Mark as optimistic
            messageType: 'form_link',
            formId: formId,
            formName: displayName
          }
        };
        
        // Add to main messages array for immediate display
        commit('addMessage', optimisticMessage);

        const payload = {
          siteID: rootState.customer?.siteID,
          patNum: patientId,
          smsText: smsMessage,
          defaultDB: siteInfo.DefaultDB,
          userODNum: rootState.customer?.user?.ODUserNum,
          employeeName: rootState.customer?.user?.Name,
          customVars: {
            eventType: 'forms_sent',
            formIDs: [formId],
            patNum: patientId,
            employeeID: rootState.customer?.userID
          }
        };

        const response = await axios.post('/send-text-msg', payload);

        // A 200 { success:false } body is a REFUSAL (opt-out / undeliverable), NOT a send.
        // Throw a tagged Error carrying the reason so the catch marks the optimistic message
        // failed and the component surfaces the reason instead of confirming a false "sent".
        if (response && response.data && response.data.success === false) {
          const optOut = optOutResultFromSendFailure(response.data);
          const refusal = new Error((optOut && optOut.reason) || response.data.message || 'The text message was not sent.');
          refusal.isSendRefusal = true;
          throw refusal;
        }

        // Update the optimistic message to show it was sent successfully
        const confirmedMessage = {
          saved: true,
          distributed: true,
          attributes: {
            ...optimisticMessage.attributes,
            optimistic: false,
            confirmed: true
          }
        };
        
        // Replace optimistic message with confirmed one
        commit('updateMessage', { messageId: optimisticMessage._id, message: confirmedMessage });
        return response.data;
      } catch (error) {
        // Log full error details including API response
        console.error('[PatientSMS] Send form link error:', error);
        if (error.response) {
          console.error('[PatientSMS] API Response Status:', error.response.status);
          console.error('[PatientSMS] API Response Data:', error.response.data);
        }
        
        // ENHANCED: Capture the specific error reason for display in UI
        const errorReason = error.response?.data?.message || error.response?.data?.error || error.response?.data || error.message || 'Failed to send form link';
        const errorCode = error.response?.data?.errorCode || error.response?.data?.error || 'UNKNOWN_ERROR';
        
        // Mark optimistic message as failed (only if it was created)
        if (optimisticMessage) {
          commit('updateMessage', { 
            messageId: optimisticMessage._id, 
            message: { 
              saved: false,
              attributes: {
                ...optimisticMessage.attributes,
                optimistic: false,
                failed: true,
                failedReason: errorReason,
                failedCode: errorCode
              }
            }
          });
        }
        
        throw error;
      }
    },

    // MIGRATED FROM conversations.js: Update conversation read status via backend API
    async updateConversationReadStatus({ rootState }, { conversationId, lastViewedMessageId }) {
      try {
        const response = await axios.post('/update-conversation-read-status', {
          conversationId: conversationId,
          lastViewedMessageId: lastViewedMessageId,
          // request-auth P3: the site we are working in, so the backend can check the caller belongs to it
          siteID: rootState.customer?.siteID
        });
        
        return response.data;
      } catch (error) {
        console.error('[ConversationsV2] Error updating conversation read status:', error);
        return null;
      }
    },

    // Mark a conversation as unread (site-level)
    async markConversationUnread({ commit, dispatch, rootState }, { roomId }) {
      try {
        // 1. Deselect the room so the snapshot unread calc doesn't force it back to 0
        //    (isSelected check runs first and overrides markedUnreadBySite)
        commit('setSelectedRoomId', null);
        commit('setMessages', []);
        commit('setMessagesLoaded', false);

        // 2. Optimistically set unread count in UI immediately
        commit('setRoomUnreadCount', { roomId, count: 1 });
        dispatch('updateSidebarNotification');

        // 3. Clear the local lastRead so the unread calc doesn't override on next snapshot
        commit('clearLastReadMs', { roomId });

        // 4. Write markedUnreadBySite directly to Firestore (no Lambda round-trip)
        const { db, doc, setDoc } = rootState;
        if (db && doc && setDoc) {
          const convoRef = doc(db, 'conversations', roomId);
          await setDoc(convoRef, {
            markedUnreadBySite: serverTimestamp(),
            unreadCount: 1
          }, { merge: true });
        }
      } catch (error) {
        console.error('[ConversationsV2] Error marking conversation as unread:', error);
      }
    },

    async sendMessageReaction({ rootState }, { reaction, remove, messageId, roomId }) {
      const { db } = rootState;
      if (!reaction?.name) return;
      const userId = rootState.customer?.siteID || rootState.customer?.userID || 'site';
      const messageRef = doc(db, 'conversations', roomId, 'messages', messageId);
      const dbAction = remove
          ? arrayRemove(userId)
          : arrayUnion(userId);
      try {
          await updateDoc(messageRef, { [`reactions.${reaction.name}`]: dbAction });
      } catch (err) {
          console.error('[conversationsV2/sendMessageReaction] Failed to update reaction:', err);
          throw err;
      }
    },

    // Update sidebar notification based on unread status
    updateSidebarNotification({ commit, getters, state }) {
      try {
        const rooms = state.rooms || [];
        const hasUnread = rooms.some(room => {
          const unreadCount = parseInt(room.unreadCount) || 0;
          return unreadCount > 0;
        });

        // The sidebar component directly watches conversationsV2Rooms
        return hasUnread;
      } catch (error) {
        console.error('[ConversationsV2] Error updating sidebar notification:', error);
        return false;
      }
    },

    /**
     * Resolve the SMS opt-out status for a list of { phone, patNum } pairs against
     * the conversations-service endpoint POST /sms-opt-out-status. Items are chunked
     * into batches of at most 200 (the endpoint's cap) and the per-batch `results`
     * arrays are concatenated in order.
     *
     * NEVER throws: a network / endpoint failure returns [] (and warns) so the
     * caller can still let the backend refuse the actual send — a thrown opt-out
     * lookup would block texting outright on a transient blip.
     *
     * @returns {Promise<Array<{ phone, patNum, blocked, code, reason, since }>>}
     */
    async fetchSmsOptOutStatus(_ctx, { siteID, items } = {}) {
      try {
        const list = Array.isArray(items) ? items : [];
        const BATCH_LIMIT = 200;
        const out = [];
        for (let i = 0; i < list.length; i += BATCH_LIMIT) {
          const batch = list.slice(i, i + BATCH_LIMIT);
          // Same base URL / relative-path pattern as the other conversations-service
          // calls in this module (/send-message, /send-text-msg, /create-conversation).
          // A 5s request timeout so a hung endpoint fails open (caught below → []) instead of
          // blocking room creation while the caller awaits.
          const response = await axios.post('/sms-opt-out-status', { siteID, items: batch }, { timeout: 5000 });
          const results = (response && response.data && response.data.results) || [];
          for (const r of results) out.push(r);
        }
        return out;
      } catch (error) {
        console.warn('[conversationsV2/fetchSmsOptOutStatus] opt-out lookup failed:', error && error.message);
        return [];
      }
    }
  },

  getters: {
    isPatientSmsMode: state => state.patientSmsMode,
    currentPatientSmsConversation: state => state.patientSmsConversation,
    patientSmsUnreadCount: state => {
      return state.patientSmsConversation?.unreadCount || 0;
    },
    patientSmsMessages: state => state.patientSmsMessages,
    patientSmsMessagesLoaded: state => state.patientSmsMessagesLoaded
  }
};

function normalizeMessageAttributes(raw) {
  try {
    if (raw && typeof raw === 'string') {
      return JSON.parse(raw);
    }
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  } catch (_) {
    return {};
  }
}

function getClientNonce(message) {
  if (!message) return null;
  const attrs = normalizeMessageAttributes(message.attributes);
  return attrs?.clientNonce || message.clientNonce || null;
}

// Helper function for file upload (same as main Conversations page)
function blobToDataUrl(blob, mimeType) {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = () => resolve('')
      if (mimeType && blob && blob.type !== mimeType) {
        try {
          blob = new Blob([blob], { type: mimeType })
        } catch (_) {}
      }
      reader.readAsDataURL(blob)
    } catch (_) {
      resolve('')
    }
  })
}


